'use client';

import { useEffect } from 'react';

import {
  BRIDGE_AVAILABLE_STORAGE_KEY,
  BRIDGE_RELAY_AVAILABLE,
  BRIDGE_RELAY_PING,
  type BridgeRelayAvailableMessage,
  type BridgeRelayPingMessage,
  handleBridgeRelayRequest,
  isBridgeRelayHostFrame,
  markTopBridgeAvailable,
} from '../lib/bridge/bridge-relay';
import { waitForJavascriptBridge, type JavascriptBridge } from '../lib/bridge/bridge-ready';

function getLocalBridge(): JavascriptBridge | null {
  if (typeof window === 'undefined') return null;
  return (
    (window as unknown as { WebViewJavascriptBridge?: JavascriptBridge }).WebViewJavascriptBridge ??
    null
  );
}

/**
 * 仅顶层挂载：把任意同源 iframe 的 Bridge 请求转到 WebViewJavascriptBridge。
 * iframe 内监听顶层「Bridge 可用」广播，写入 sessionStorage 供 hasBridge() 使用。
 *
 * Native 拒绝非主 frame 的 callHandler（not_main_frame）；
 * 子 frame 即便被注入了 WebViewJavascriptBridge 也不得直调。
 */
export function BridgeRelayHost() {
  useEffect(() => {
    if (typeof window === 'undefined' || !isBridgeRelayHostFrame()) return;

    let disposed = false;
    let bridge: JavascriptBridge | null = null;

    const publish = (available: boolean) => {
      if (disposed) return;
      markTopBridgeAvailable(available);
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const ping = event.data as BridgeRelayPingMessage | null;
      if (ping?.type === BRIDGE_RELAY_PING) {
        const ready = !!(bridge ?? getLocalBridge());
        if (ready) {
          bridge = bridge ?? getLocalBridge();
          publish(true);
        } else if (event.source && typeof (event.source as Window).postMessage === 'function') {
          const message: BridgeRelayAvailableMessage = {
            type: BRIDGE_RELAY_AVAILABLE,
            available: false,
          };
          (event.source as Window).postMessage(message, window.location.origin);
        }
        return;
      }
      handleBridgeRelayRequest(event, () => bridge ?? getLocalBridge());
    };

    window.addEventListener('message', onMessage);

    const existing = getLocalBridge();
    if (existing) {
      bridge = existing;
      publish(true);
    }

    void waitForJavascriptBridge(30_000).then((ready) => {
      if (disposed) return;
      if (ready) {
        bridge = ready;
        publish(true);
      }
    });

    document.addEventListener(
      'miaobi-jsbridge-ready',
      () => {
        const next = getLocalBridge();
        if (!next || disposed) return;
        bridge = next;
        publish(true);
      },
      { once: true },
    );

    const retryId = window.setInterval(() => {
      if (disposed) return;
      if (bridge || getLocalBridge()) {
        bridge = bridge ?? getLocalBridge();
        publish(true);
      }
    }, 2_000);
    window.setTimeout(() => window.clearInterval(retryId), 20_000);

    return () => {
      disposed = true;
      window.clearInterval(retryId);
      window.removeEventListener('message', onMessage);
      publish(false);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || isBridgeRelayHostFrame()) return;

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      try {
        if (event.source !== window.parent && event.source !== window.top) return;
      } catch {
        if (event.source !== window.parent) return;
      }
      const data = event.data as BridgeRelayAvailableMessage | null;
      if (!data || data.type !== BRIDGE_RELAY_AVAILABLE) return;
      try {
        if (data.available) {
          sessionStorage.setItem(BRIDGE_AVAILABLE_STORAGE_KEY, '1');
        } else {
          sessionStorage.removeItem(BRIDGE_AVAILABLE_STORAGE_KEY);
        }
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return null;
}
